import { TypeRegister } from 'practice/constants/type-register';
import { NewResidenceVM } from 'practice/view-models/practice-edit-residence';
import { regexOnlyNumber } from 'shell/constants/regex';
import { ItalianVM } from 'shell/view-models/register-office-practice-vm';
import { ObjectShape } from 'yup/lib/object';
import { HouseOccupationType, TipoOccupazioneLegittima } from 'practice/constants/house-occupation';
import * as yup from 'yup';

/**
 * Funzione di validazione dello schema
 * @param {boolean} isCadastralFieldsMandatory dati catastali obbligatori
 * @param {TypeRegister} typeRegister tipo registro
 * @returns {yup.AnyObjectSchema}
 */
export const validationSchema = (
  isCadastralFieldsMandatory?: boolean,
  typeRegister?: TypeRegister
): yup.AnyObjectSchema => {
  let schema: ObjectShape = {};

  const isTypeRegisterImmigration = typeRegister === TypeRegister.Immigration;

  const isEmigration = typeRegister === TypeRegister.Emigration;

  schema = {
    checkedResidence: yup.boolean().nullable(),
    italian: yup.object().shape({
      assensoOspitante: yup.boolean().nullable(),
      fiscalCodeProprietario: yup.string().nullable(),
      surnameProprietario: yup.string().nullable(),
      nameProprietario: yup.string().nullable(),
      fiscalCodeOspitante: yup.string().nullable(),
      surnameOspitante: yup.string().nullable(),
      nameOspitante: yup.string().nullable(),
      fiscalCodeERP: yup.string().nullable(),
      fiscalCodeStruttura: yup.string().nullable(),
      nameStruttura: yup.string().nullable(),
      municipalityResidenceStruttura: yup.object().nullable(),
      addressStruttura: yup.string().nullable(),
      section: yup.string().nullable(),
      sheet: yup
        .string()
        .nullable()
        .matches(regexOnlyNumber, 'Il campo è numerico')
        .max(3, 'Massimo 3 byte'),
      particle: yup
        .string()
        .nullable()
        .matches(regexOnlyNumber, 'Il campo è numerico')
        .max(8, 'Massimo 8 byte'),
      sub: yup
        .string()
        .nullable()
        .matches(regexOnlyNumber, 'Il campo è numerico')
        .max(4, 'Massimo 4 byte'),
      cat: yup.string().nullable(),
      municipality: yup.object().nullable(),
      cap: yup.string().nullable(),
      addressAuto: yup.object().nullable(),
      number: yup.string().nullable(),
      houseOccupation: yup.string().nullable(),
      officeContract: yup.object().nullable(),
      numberContract: yup.string().matches(regexOnlyNumber, 'Il campo è numerico').nullable(),
      dateContract: yup.date().nullable().typeError('Data non valida'),
    }),
  };
  return yup
    .object({
      ...schema,
    })
    .test('municipality', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (!castedValues.checkedResidence && !castedValues.italian?.municipality?.description) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.municipality');
      }

      return true;
    })
    .test('addressAuto', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (
        !castedValues.checkedResidence &&
        !castedValues.italian?.addressAuto?.description &&
        !isEmigration
      ) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.addressAuto');
      }

      return true;
    })
    .test('cap', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (!castedValues.checkedResidence && !castedValues.italian?.cap && !isEmigration) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.cap');
      }

      return true;
    })
    .test('number', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (!castedValues.checkedResidence && !castedValues.italian?.number && !isEmigration) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.number');
      }

      return true;
    })
    .test('sheet', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (
        castedValues.italian?.section?.toUpperCase() === 'NC' ||
        castedValues.italian?.section?.toUpperCase() === 'NP'
      ) {
        return true;
      }

      if (
        !castedValues.checkedResidence &&
        !castedValues.italian?.sheet &&
        isCadastralFieldsMandatory &&
        isTypeRegisterImmigration &&
        !castedValues?.italian?.isHomeless
      ) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.sheet');
      }

      return true;
    })
    .test('particle', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (
        castedValues.italian?.section?.toUpperCase() === 'NC' ||
        castedValues.italian?.section?.toUpperCase() === 'NP'
      ) {
        return true;
      }

      if (
        !castedValues.checkedResidence &&
        !castedValues.italian?.particle &&
        isCadastralFieldsMandatory &&
        isTypeRegisterImmigration &&
        !castedValues?.italian?.isHomeless
      ) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.particle');
      }

      return true;
    })
    .test('sub', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (
        castedValues.italian?.section?.toUpperCase() === 'NC' ||
        castedValues.italian?.section?.toUpperCase() === 'NP'
      ) {
        return true;
      }

      if (
        !castedValues.checkedResidence &&
        !castedValues.italian?.sub &&
        isCadastralFieldsMandatory &&
        isTypeRegisterImmigration &&
        !castedValues?.italian?.isHomeless
      ) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.sub');
      }

      return true;
    })
    .test('', 'Controllo Agenzia entrate', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (
        castedValues.italian?.houseOccupationType !==
          HouseOccupationType.IntestatarioContrattoAgenziaEntrate &&
        castedValues.italian?.houseOccupationType !== HouseOccupationType.ComodatarioAgenziaEntrate
      ) {
        return true;
      }

      const datiContratto = ['numberContract', 'dateContract', 'officeContract'] as Array<
        keyof ItalianVM
      >;

      const datiCatastali = ['section', 'sheet', 'particle', 'sub', 'cat'] as Array<
        keyof ItalianVM
      >;

      const hasDatiCatastaliPresent = datiCatastali.every((item) => {
        if (!castedValues.italian) {
          return false;
        }

        return !!castedValues.italian[item];
      });

      const hasSomeDatiCatastaliPresent = datiCatastali.some((item) => {
        if (!castedValues.italian) {
          return false;
        }

        return !!castedValues.italian[item];
      });

      const hasSomeDatiContrattoPresent = datiContratto.some((item) => {
        if (!castedValues.italian) {
          return false;
        }

        return !!castedValues.italian[item];
      });

      const hasDatiContrattoPresent = datiContratto.every((item) => {
        if (!castedValues.italian) {
          return false;
        }

        return !!castedValues.italian[item];
      });

      if (!castedValues.checkedResidence && !hasDatiCatastaliPresent) {
        const errors = datiCatastali
          .map((key) => {
            const value = (castedValues?.italian || {})[key];
            if (value) {
              return null;
            } // Successful validation, no error

            return new yup.ValidationError('Il campo è obbligatorio', value, `italian.${key}`);
          })
          .filter((item) => !!item) as unknown as Array<yup.ValidationError>;
        if (errors.length === 0 || hasSomeDatiContrattoPresent) {
          return true;
        }
      }

      if (!castedValues.checkedResidence && !hasDatiContrattoPresent) {
        const errors = datiContratto
          .map((key) => {
            const value = (castedValues?.italian || {})[key];
            if (value) {
              return null;
            } // Successful validation, no error

            return new yup.ValidationError('Il campo è obbligatorio', value, `italian.${key}`);
          })
          .filter((item) => !!item) as unknown as Array<yup.ValidationError>;
        if (errors.length === 0 || hasSomeDatiCatastaliPresent) {
          return true;
        }
        return new yup.ValidationError(errors);
      }

      return true;
    })
    .test('Dati Struttura', 'Controllo', (values) => {
      // Controllo sui dati struttura
      const castedValues = values as unknown as NewResidenceVM;
      if (
        castedValues.italian?.houseOccupationType === HouseOccupationType.OccupanteLegittimo &&
        castedValues.italian?.occupantiLegittimi ===
          TipoOccupazioneLegittima.OspiteETSStrutturaSanitariaOPrivate
      ) {
        const keysDatiStruttura = [
          'fiscalCodeStruttura',
          'nameStruttura',
          'municipalityResidenceStruttura',
          'addressStruttura',
        ] as Array<keyof ItalianVM>;

        const errors = keysDatiStruttura
          .map((key) => {
            const value = (castedValues?.italian || {})[key];
            if (value) {
              return null;
            }

            return new yup.ValidationError('Il campo è obbligatorio', value, `italian.${key}`);
          })
          .filter((item) => !!item) as unknown as Array<yup.ValidationError>;
        if (errors.length === 0) {
          return true;
        }
        return new yup.ValidationError(errors);
      }
      return true;
    });
};
